import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import InlineCode from './inline-code';
import { Box, Icon, Link, TextBody, TextHeading, TextNotation, Tooltip } from '@houzz/ui';
import ArrowRightCta from '@houzz/icons/ArrowRightCta';
import { withPrefix } from 'gatsby';
import { hzNavigate } from '../utils/navigation';

const StyledTableRow = styled.tr``;

const PropName = styled.code`
    display: inline-block;
    font-size: 0.72rem;
    padding: 2px;
`;

const CodePill = styled.code`
    display: inline-block;
    font-size: 0.72rem;
    line-height: 1.3;
    padding: 2px 4px;
    background: #f8f8f8;
    border: 1px solid #eee;
    border-radius: 4px;
    color: #666;
`;

export const StyledTable = styled.table`
    width: 100%;
    table-layout: fixed;
    margin-bottom: 20px;
`;

export const StyledTableCell = styled.td`
    padding: 8px 12px;
    font-size: 14px;
    border-bottom: 1px solid #efefef;
    word-break: break-word;
    width: ${(props) => props.width || 'auto'};

    &:first-child {
        padding-left: 0;
    }
    &:last-child {
        padding-right: 0;
    }

    ${InlineCode} {
        margin: 2px;
    }
`;

export const StyledTableHead = styled(StyledTableCell)`
    font-size: 16px;
    font-weight: bold;
    white-space: nowrap;
`;

const getType = ({ name, value }) => {
    if (name !== 'enum') {
        return <Box as={CodePill}>{name}</Box>;
    }

    return (
        <Box as={PropName} color="purple.shade1">
            {value.map((v) => v.value).join(' | ')}
        </Box>
    );
};

const baseStyledSystemProps = ['common', 'typography', 'border', 'layout', 'position', 'flex', 'grid'];

const PropTable = ({ docData = [], of = '', description = '', system = [] }) => {
    const propData = docData.find((doc) => doc.displayName === of);
    if (!propData) {
        return null;
    }

    // eslint-disable-next-line no-unused-vars
    const { props, description: componentDescription } = propData;
    const propsArray = Object.values(props);

    return (
        <>
            <Box as="code" color="magenta.tint1" fontSize="tiny">
                {of}
            </Box>
            {/*componentDescription && (
                <Box bg="bg.tertiary" p="M" width="80%">
                    <TextBody size="small" color="content.secondary" style={{ whiteSpace: 'pre' }}>
                        {componentDescription}
                    </TextBody>
                </Box>
            )*/}
            {!!(system && system.length) && (
                <Box as="section">
                    <TextHeading as="h5" size="xSmall" fontSize="small" my="XS">
                        System Props
                    </TextHeading>
                    <TextBody>{description}</TextBody>
                    <SystemProps systemProps={system} />
                </Box>
            )}

            <StyledTable className="prop-table">
                <thead>
                    <StyledTableRow>
                        <StyledTableHead width="180px">Name</StyledTableHead>
                        <StyledTableHead width="40%">Type</StyledTableHead>
                        <StyledTableHead width="50%">Description</StyledTableHead>
                    </StyledTableRow>
                </thead>
                <tbody>
                    {propsArray.map((prop) => {
                        const type = getType(prop.type);
                        const defaultProp = prop.defaultValue && prop.defaultValue.value;

                        return (
                            <StyledTableRow key={prop.name}>
                                <StyledTableCell>
                                    <PropName>
                                        {prop.name}
                                        {prop.required && <TextNotation color="red.core">*</TextNotation>}
                                    </PropName>
                                </StyledTableCell>
                                <StyledTableCell>{type}</StyledTableCell>
                                <StyledTableCell>
                                    {prop.description || '-'}{' '}
                                    {defaultProp !== null && (
                                        <>
                                            <br />
                                            <CodePill>{defaultProp}</CodePill>
                                        </>
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        );
                    })}
                </tbody>
            </StyledTable>
        </>
    );
};

PropTable.propTypes = {
    description: PropTypes.string,
    docData: PropTypes.array,
    of: PropTypes.string,
    system: PropTypes.arrayOf(['box', ...baseStyledSystemProps]),
};

export default PropTable;

const SystemProps = ({ systemProps = [] }) => {
    if (systemProps[0] === 'box') {
        systemProps.shift();
        systemProps.unshift(...baseStyledSystemProps);
    }

    return (
        <Box as="ul" m="none" p="none" display="flex" mt="XS" mb="XL" style={{ listStyle: 'none', gap: '8px' }}>
            {systemProps.map((prop) => {
                let tooltipLabel = '';
                switch (prop) {
                    case 'common':
                        tooltipLabel = 'margin, padding, color, bg, display';
                        break;
                    case 'typography':
                        tooltipLabel = 'fontSize, lineHeight, fontStyle, letterSpacing, textAlign';
                        break;
                    case 'border':
                        tooltipLabel = 'border, boxShadow, textShadow';
                        break;
                    case 'layout':
                        tooltipLabel = 'width, height, display, overflow, verticalAlign';
                        break;
                    case 'position':
                        tooltipLabel = 'position';
                        break;
                    case 'flex':
                        tooltipLabel = 'align, justify, flex, order';
                        break;
                    case 'grid':
                        tooltipLabel = 'grid';
                        break;
                }
                return (
                    <Box as="li" key={prop}>
                        <Tooltip labelText={tooltipLabel} colorVariant="neutral" shouldWrapTrigger={true}>
                            <span>
                                <CodePill>{prop.toUpperCase()}</CodePill>
                            </span>
                        </Tooltip>
                    </Box>
                );
            })}
            <Box as="li" ml="auto">
                <Link
                    href={withPrefix('/components/system-props')}
                    size="xSmall"
                    onClick={(event) => hzNavigate(event, '/components/system-props')}
                >
                    Learn More
                    <Icon as={ArrowRightCta} ml="XXS" />
                </Link>
            </Box>
        </Box>
    );
};

SystemProps.propTypes = {
    systemProps: PropTypes.array,
};
